import { computed, reactive, watch } from 'vue';
import type { AfterMarketRow, AfterMarketSaveCallbacks } from '../types';
import { createAfterMarketTableStore } from './createAfterMarketTableStore';

type Mode = 'read-only' | 'edit';

export interface UseAfterMarketTableLogicProps {
  initialRows: AfterMarketRow[];
  mode?: Mode;
}

export interface AfterMarketTableState {
  rows: AfterMarketRow[];
  isEditing: boolean;
  isSaving: boolean;
  sortedRows: AfterMarketRow[];
  newRowDraft: { text: string; date: string };
  mode: Mode;
}

export interface AfterMarketTableAction {
  startEdit: () => void;
  requestSave: () => void;
  cancel: () => void;
  addRow: () => void;
  deleteRow: (id: string) => void;
  updateRow: (id: string, field: keyof AfterMarketRow, value: string) => void;
  updateNewRow: (field: 'text' | 'date', value: string) => void;
}

export const useAfterMarketTableLogic = (
  props: UseAfterMarketTableLogicProps,
  emit: (
    event: 'save',
    rows: AfterMarketRow[],
    callbacks: AfterMarketSaveCallbacks,
  ) => void,
): { state: AfterMarketTableState; action: AfterMarketTableAction } => {
  const store = createAfterMarketTableStore(props.initialRows);

  watch(
    () => props.initialRows,
    (newRows) => {
      if (store.status.value !== 'editing') {
        store.reset(newRows);
      }
    },
  );

  const sortedRows = computed(() =>
    [...store.rows].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.localeCompare(b.date);
    }),
  );

  const action: AfterMarketTableAction = {
    startEdit: () => {
      store.dispatch({ type: 'start-edit' });
    },
    requestSave: () => {
      const savedRows = store.dispatch({ type: 'request-save' });
      if (savedRows)
        emit('save', savedRows, {
          onSuccess: () => store.dispatch({ type: 'complete-save' }),
          onError: () => store.dispatch({ type: 'revert-save' }),
        });
    },
    cancel: () => {
      store.dispatch({ type: 'cancel' });
    },
    addRow: () => {
      store.dispatch({ type: 'add-row' });
    },
    deleteRow: (id: string) => {
      store.dispatch({ type: 'delete-row', id });
    },
    updateRow: (id: string, field: keyof AfterMarketRow, value: string) => {
      store.dispatch({ type: 'update-row', id, field, value: value.trim() });
    },
    updateNewRow: (field: 'text' | 'date', value: string) => {
      store.dispatch({ type: 'set-new-row-field', field, value });
    },
  };

  return {
    state: reactive({
      rows: store.rows,
      isEditing: computed(() => store.status.value === 'editing'),
      isSaving: computed(() => store.status.value === 'saving'),
      sortedRows,
      newRowDraft: store.newRowDraft,
      mode: computed(() => props.mode ?? 'read-only'),
    }),
    action,
  };
};
